import { useState } from "react";
import { api } from "@/App";
import { Sparkles, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";

/**
 * AI Doubt Solver - 1-click button that asks GPT to explain a specific
 * MCQ to the student in plain English (great for ESL learners).
 * Drop it under any answered question.
 */
const DoubtSolver = ({ questionId, selectedOptionId, testIdPrefix = "doubt" }) => {
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [showFollowUp, setShowFollowUp] = useState(false);

  const askAI = async (userQuestion = null) => {
    setLoading(true);
    try {
      const res = await api.post("/chat/doubt", {
        question_id: questionId,
        selected_option_id: selectedOptionId || null,
        user_question: userQuestion || null,
      });
      setExplanation(res.explanation || "");
    } catch (e) {
      console.error("Doubt solver failed", e);
      if (e?.response?.status === 402) {
        setExplanation(
          "You've used your 3 free AI tutor explanations for today. Upgrade to Pro for unlimited AI tutoring - or come back tomorrow."
        );
      } else {
        setExplanation(
          "Sorry, the tutor is taking a break. Please try again in a moment."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = (e) => {
    e.preventDefault();
    if (!followUp.trim()) return;
    askAI(followUp.trim());
    setFollowUp("");
  };

  if (!explanation && !loading) {
    return (
      <button
        data-testid={`${testIdPrefix}-ask-btn`}
        onClick={() => askAI()}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/15 hover:bg-primary/25 text-primary text-sm font-medium transition-all border border-primary/30"
      >
        <Sparkles className="w-4 h-4" />
        Ask the tutor: explain this in simple words
      </button>
    );
  }

  return (
    <div
      data-testid={`${testIdPrefix}-panel`}
      className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary" />
        <p className="text-sm font-semibold text-primary">Tutor</p>
        {loading && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
      </div>

      {loading && !explanation ? (
        <div className="space-y-2">
          <div className="h-3 bg-secondary rounded w-full animate-pulse" />
          <div className="h-3 bg-secondary rounded w-5/6 animate-pulse" />
          <div className="h-3 bg-secondary rounded w-4/6 animate-pulse" />
        </div>
      ) : (
        <div className="prose prose-invert prose-sm max-w-none text-sm">
          <ReactMarkdown
            components={{
              // eslint-disable-next-line react/no-unstable-nested-components
              strong: ({ children }) => (
                <strong className="text-primary font-semibold">{children}</strong>
              ),
              // eslint-disable-next-line react/no-unstable-nested-components
              ul: ({ children }) => (
                <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>
              ),
              // eslint-disable-next-line react/no-unstable-nested-components
              p: ({ children }) => <p className="mb-2">{children}</p>,
            }}
          >
            {explanation}
          </ReactMarkdown>
        </div>
      )}

      {/* Follow-up */}
      {!loading && explanation && (
        <div className="mt-3 pt-3 border-t border-border">
          {!showFollowUp ? (
            <button
              data-testid={`${testIdPrefix}-followup-btn`}
              onClick={() => setShowFollowUp(true)}
              className="text-xs text-primary hover:underline"
            >
              Still confused? Ask a follow-up question →
            </button>
          ) : (
            <form onSubmit={handleFollowUp} className="flex gap-2">
              <input
                data-testid={`${testIdPrefix}-followup-input`}
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                placeholder="e.g. but why not option C?"
                className="flex-1 px-3 py-2 rounded-lg bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
                autoFocus
              />
              <button
                type="submit"
                disabled={!followUp.trim()}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
              >
                Ask
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default DoubtSolver;
